import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { toActivitiesUrl } from '@/utils/drillthrough';
import type { CriticalActivityRow, CriticalityBand } from '@/utils/analyticsCompute';
import { isCompleted, NEAR_CRITICAL_FLOAT_HOURS, CRITICAL_TABLE_FLOAT_HOURS } from '@/utils/analyticsCompute';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

/* ─── Filter types ─── */

/** Float bucket filter labels in hours (calendar-agnostic). */
export type FloatBucketFilter = 'all' | '<=0' | '0-40' | '40-80' | '>80';
/** Criticality band filter. */
export type BandFilter = 'all' | 'critical' | 'near-critical' | 'monitor';
export type StatusFilter = 'all' | 'not_started' | 'in_progress' | 'complete';

export interface CriticalActivitiesTableProps {
  activities: CriticalActivityRow[];
  loading?: boolean;
  maxRows?: number;
}

/* ─── Helpers ─── */

function formatDate(s: string | undefined): string {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleDateString(undefined, { dateStyle: 'short' });
  } catch {
    return '—';
  }
}

function pct(a: CriticalActivityRow): number | string {
  const p = (a as CriticalActivityRow & { percent_complete?: number }).percent_complete
    ?? (a as CriticalActivityRow & { effective_percent_complete?: number }).effective_percent_complete
    ?? (a as CriticalActivityRow & { phys_complete_pct?: number }).phys_complete_pct;
  if (p != null) return typeof p === 'number' ? p : Number(p);
  return '—';
}

function floatHrs(a: CriticalActivityRow): number | null {
  const hrs = (a as Record<string, unknown>).total_float_hr_cnt;
  if (hrs === undefined || hrs === null) return null;
  const n = Number(hrs);
  return Number.isNaN(n) ? null : n;
}

function matchFloatBucket(floatHours: number, bucket: FloatBucketFilter): boolean {
  if (bucket === 'all') return true;
  if (bucket === '<=0') return floatHours <= 0;
  if (bucket === '0-40') return floatHours > 0 && floatHours <= 40;
  if (bucket === '40-80') return floatHours > 40 && floatHours <= 80;
  if (bucket === '>80') return floatHours > 80;
  return true;
}

function matchBand(band: CriticalityBand, filter: BandFilter): boolean {
  if (filter === 'all') return true;
  return band === filter;
}

function matchStatus(a: CriticalActivityRow, status: StatusFilter): boolean {
  if (status === 'all') return true;
  const completed = isCompleted(a);
  const pctVal = pct(a);
  const pctNum = typeof pctVal === 'number' ? pctVal : pctVal === '—' ? 0 : Number(pctVal);
  if (status === 'complete') return completed || pctNum >= 100;
  if (status === 'not_started') return !completed && (pctNum === 0 || pctVal === '—');
  if (status === 'in_progress') return !completed && pctNum > 0 && pctNum < 100;
  return true;
}

/* ─── Badge variant mapping ─── */
const bandBadgeVariant: Record<CriticalityBand, 'critical' | 'warning' | 'secondary'> = {
  'critical': 'critical',
  'near-critical': 'warning',
  'monitor': 'secondary',
};
const bandLabel: Record<CriticalityBand, string> = {
  'critical': 'Critical',
  'near-critical': 'Near-Crit',
  'monitor': 'Monitor',
};

/* ─── Component ─── */

export const CriticalActivitiesTable: React.FC<CriticalActivitiesTableProps> = ({
  activities,
  loading = false,
  maxRows = 100,
}) => {
  const [floatBucket, setFloatBucket] = useState<FloatBucketFilter>('all');
  const [bandFilter, setBandFilter] = useState<BandFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  /* ── Summary counts (unfiltered) ── */
  const counts = useMemo(() => {
    let critical = 0;
    let nearCritical = 0;
    let monitor = 0;
    for (const a of activities) {
      if (a.criticalityBand === 'critical') critical++;
      else if (a.criticalityBand === 'near-critical') nearCritical++;
      else monitor++;
    }
    return { critical, nearCritical, monitor, total: activities.length };
  }, [activities]);

  const rows = useMemo(() => {
    let list = activities.filter(
      (a) =>
        matchFloatBucket(a.floatHours, floatBucket) &&
        matchBand(a.criticalityBand, bandFilter) &&
        matchStatus(a, statusFilter),
    );
    list = [...list].sort((a, b) => a.floatHours - b.floatHours).slice(0, maxRows);
    return list;
  }, [activities, floatBucket, bandFilter, statusFilter, maxRows]);

  if (loading) {
    return (
      <div className="p-3">
        <p className="text-xs text-muted-foreground">Loading…</p>
      </div>
    );
  }

  /* ── Summary strip ── */
  const summaryStrip = (
    <div className="flex items-center gap-3 mb-2 text-xs tabular-nums">
      <span className="font-medium text-foreground">{counts.total} activities</span>
      <span className="text-muted-foreground">·</span>
      <span className="text-destructive font-medium">{counts.critical} Critical</span>
      <span className="text-muted-foreground">·</span>
      <span className="text-amber-600 dark:text-amber-400 font-medium">{counts.nearCritical} Near-Critical</span>
      {counts.monitor > 0 && (
        <>
          <span className="text-muted-foreground">·</span>
          <span className="text-muted-foreground font-medium">{counts.monitor} Monitor</span>
        </>
      )}
    </div>
  );

  /* ── Filter bar ── */
  const filterBar = (
    <div className="flex flex-wrap gap-1.5 mb-2 text-xs items-center">
      {/* Band filter */}
      <span className="text-muted-foreground font-medium">Band:</span>
      {(['all', 'critical', 'near-critical', 'monitor'] as const).map((b) => (
        <Button
          key={b}
          variant={bandFilter === b ? 'default' : 'outline'}
          size="sm"
          className="h-6 px-2 text-[11px]"
          onClick={() => setBandFilter(b)}
        >
          {b === 'all' ? 'All' : b === 'critical' ? 'Critical' : b === 'near-critical' ? 'Near-Crit' : 'Monitor'}
        </Button>
      ))}

      <span className="text-muted-foreground font-medium ml-2">Float (hr):</span>
      {(['all', '<=0', '0-40', '40-80', '>80'] as const).map((b) => (
        <Button
          key={b}
          variant={floatBucket === b ? 'default' : 'outline'}
          size="sm"
          className="h-6 px-2 text-[11px]"
          onClick={() => setFloatBucket(b)}
        >
          {b === 'all' ? 'All' : b}
        </Button>
      ))}

      <span className="text-muted-foreground font-medium ml-2">Status:</span>
      {(['all', 'not_started', 'in_progress', 'complete'] as const).map((s) => (
        <Button
          key={s}
          variant={statusFilter === s ? 'default' : 'outline'}
          size="sm"
          className="h-6 px-2 text-[11px]"
          onClick={() => setStatusFilter(s)}
        >
          {s === 'all' ? 'All' : s === 'not_started' ? 'Not started' : s === 'in_progress' ? 'In progress' : 'Complete'}
        </Button>
      ))}
    </div>
  );

  return (
    <div className="p-3">
      {summaryStrip}
      {filterBar}
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No activities match current filters (table includes float ≤{CRITICAL_TABLE_FLOAT_HOURS} hr).
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="text-left py-1.5 px-2 text-muted-foreground font-medium w-[72px]">Band</th>
                <th className="text-left py-1.5 px-2 text-muted-foreground font-medium">Activity ID</th>
                <th className="text-left py-1.5 px-2 text-muted-foreground font-medium">Name</th>
                <th className="text-left py-1.5 px-2 text-muted-foreground font-medium">WBS</th>
                <th className="text-left py-1.5 px-2 text-muted-foreground font-medium">Start</th>
                <th className="text-left py-1.5 px-2 text-muted-foreground font-medium">Finish</th>
                <th className="text-right py-1.5 px-2 text-muted-foreground font-medium" title="Total float in hours from P6">Float (hr)</th>
                <th className="text-right py-1.5 px-2 text-muted-foreground font-medium">% Comp</th>
                <th className="w-16 py-1.5 px-2 text-muted-foreground font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const id = (r as CriticalActivityRow & { id?: string }).id ?? (r as Record<string, unknown>).task_id as string;
                const wbsId = String((r as CriticalActivityRow & { wbs_id?: string }).wbs_id ?? '');
                const viewHref = toActivitiesUrl({ wbs_id: wbsId || undefined, critical: true, sort: 'total_float_asc' });
                return (
                  <tr key={id} className="border-b border-border/40 hover:bg-muted/20">
                    <td className="py-1.5 px-2">
                      <Badge variant={bandBadgeVariant[r.criticalityBand]} size="sm">
                        {bandLabel[r.criticalityBand]}
                      </Badge>
                    </td>
                    <td className="py-1.5 px-2 text-foreground">{id}</td>
                    <td className="py-1.5 px-2 text-foreground truncate max-w-[180px]" title={(r as CriticalActivityRow & { name?: string }).name}>
                      {(r as CriticalActivityRow & { name?: string }).name ?? '—'}
                    </td>
                    <td className="py-1.5 px-2 text-muted-foreground truncate max-w-[100px]">
                      {((r as CriticalActivityRow & { wbs_name?: string }).wbs_name ?? wbsId) || '—'}
                    </td>
                    <td className="py-1.5 px-2 text-muted-foreground tabular-nums">
                      {formatDate((r as CriticalActivityRow).early_start_date ?? (r as Record<string, unknown>).target_start_date as string)}
                    </td>
                    <td className="py-1.5 px-2 text-muted-foreground tabular-nums">
                      {formatDate((r as CriticalActivityRow).early_end_date ?? (r as Record<string, unknown>).target_end_date as string)}
                    </td>
                    <td
                      className={`py-1.5 px-2 tabular-nums text-right ${
                        (floatHrs(r) ?? 0) < 0 ? 'text-destructive' : (floatHrs(r) ?? 0) <= NEAR_CRITICAL_FLOAT_HOURS ? 'text-amber-600 dark:text-amber-400' : 'text-foreground'
                      }`}
                      title="Total float (hours)"
                    >
                      {floatHrs(r) != null ? `${Number(floatHrs(r)).toFixed(1)}` : '—'}
                    </td>
                    <td className="py-1.5 px-2 text-right tabular-nums text-muted-foreground">{pct(r)}</td>
                    <td className="py-1.5 px-2 text-right">
                      <Link to={viewHref} className="inline-flex items-center justify-end gap-0.5 text-primary text-[11px] font-medium hover:underline" title="View activities">
                        View
                        <ChevronRight className="h-3 w-3" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="text-[10px] text-muted-foreground mt-1.5">
            Critical = driving path or float ≤ 0 hr · Near-Critical = float 0–{NEAR_CRITICAL_FLOAT_HOURS} hr (≤5d) · Monitor = float {NEAR_CRITICAL_FLOAT_HOURS}–{CRITICAL_TABLE_FLOAT_HOURS} hr (5–10d)
          </p>
        </div>
      )}
    </div>
  );
};
